import React, { useContext } from 'react';
import {
  getAppointmentSchedulingFlowTransitionUrl,
  getAppointmentSchedulingFlowUrl,
  logger,
  toast,
  useRequest,
  QueryUtils,
  getAppointmentSchedulingFlowForClinicianRoleUrl,
  isTherapist,
  UserRoles,
  TherapyOption,
  BookingSchedulingFlowDto,
  getRoleName,
  PatientSurveyDto,
  QuestionDto,
  PatientAnswerDto,
  SurveyStatuses,
  SurveyTypesEnum,
  usePatientSurvey,
  useAuth,
} from '@monument/core';
import { useOnboarding } from '@services/onboarding';
import { AppointmentActionEnum } from '@shared/enums/appointment-action.enum';

const { useQuery } = QueryUtils;

type AppointmentSchedulingTransitionEvent = string;
type AppointmentSchedulingTransitionPayload =
  | ({ questionId?: string } & Record<string, any>)
  | undefined;
interface AppointmentSchedulingTransitionOptions {
  onBeforeSync?: (newAppointmentScheduling?: BookingSchedulingFlowDto) => Promise<unknown>;
  onAfterSync?: (newAppointmentScheduling?: BookingSchedulingFlowDto) => Promise<unknown>;
  handleErrors?: boolean;
}

interface IAppointmentSchedulingContext {
  appointmentSchedulingFlow: BookingSchedulingFlowDto | undefined;
  transition: (
    event: AppointmentSchedulingTransitionEvent,
    payload?: AppointmentSchedulingTransitionPayload,
    options?: AppointmentSchedulingTransitionOptions,
  ) => Promise<void>;
  initSchedulingFlow: (clinicianRole: number) => Promise<void>;
  /**
   * Specific appointment flow type, initiated differently, used to determine how we fetch, etc.
   * */
  isClinicianChangeFlow: boolean;
  loading: boolean;
  patientSurvey: PatientSurveyDto | undefined;
  intakeSections: Record<string, QuestionDto[]> | undefined;
  intakeAnswers: PatientAnswerDto[] | undefined;
  refreshPatientSurvey: () => Promise<PatientSurveyDto>;
}

const AppointmentSchedulingContext = React.createContext<IAppointmentSchedulingContext>(
  {} as IAppointmentSchedulingContext,
);
export const useAppointmentScheduling = () => useContext(AppointmentSchedulingContext);

/**
 * Patient app specific appointment scheduling context
 */
export const AppointmentSchedulingContextProvider = ({ children }) => {
  const { user } = useAuth();
  const { onboarding } = useOnboarding();
  const { query } = useQuery();
  const { paymentSelection } = onboarding?.store || {};
  const isClinicianChangeFlow = query.action === AppointmentActionEnum.ChangeClinician;

  const {
    loading: patientSurveyLoading,
    survey,
    patientSurvey,
    patientSurveyAnswers: intakeAnswers,
    refreshPatientSurvey,
  } = usePatientSurvey({
    patientId: user?.accountId,
    surveyType: SurveyTypesEnum.Intake,
    surveyStatuses: [SurveyStatuses.Ongoing, SurveyStatuses.Finished],
  });

  const flowReq = useRequest<BookingSchedulingFlowDto, [UserRoles]>(
    clinicianRole => ({
      url: getAppointmentSchedulingFlowForClinicianRoleUrl('me', clinicianRole),
      withErrorsHandler: false,
    }),
    {
      manual: true,
    },
  );

  const initFlowReq = useRequest<
    BookingSchedulingFlowDto,
    [{ clinicianRole: UserRoles; therapyOption?: TherapyOption }]
  >(
    data => {
      return {
        url: getAppointmentSchedulingFlowUrl(),
        method: 'post',
        data,
        withErrorsHandler: false,
      };
    },
    {
      manual: true,
    },
  );

  const transitionReq = useRequest<
    BookingSchedulingFlowDto,
    [
      AppointmentSchedulingTransitionEvent,
      AppointmentSchedulingTransitionPayload,
      AppointmentSchedulingTransitionOptions | undefined,
    ]
  >(
    (
      event: AppointmentSchedulingTransitionEvent,
      payload: AppointmentSchedulingTransitionPayload,
      options?: AppointmentSchedulingTransitionOptions,
    ) => ({
      url: getAppointmentSchedulingFlowTransitionUrl(),
      method: 'post',
      data: {
        id: flowReq?.data!.id,
        event,
        payload,
      },
      withErrorsHandler: options?.handleErrors,
    }),
    {
      manual: true,
      onError: (error, [_event, _payload, _options]) => {
        if (_options?.handleErrors) {
          // Invalid state transitions shouldn't be possible if everything is working correctly. On the
          // off-chance a user gets into an invalid state, we should restore to whatever stage the
          // backend thinks we should be in.
          logger.error('useAppointmentScheduling#transition', error);
          flowReq.refresh();
          toast.error({
            title: 'Something went wrong',
            description:
              'Please reload the page and try again, or contact support if the problem continues.',
          });
        }
      },
    },
  );

  const transition = async (
    event: AppointmentSchedulingTransitionEvent,
    payload?: AppointmentSchedulingTransitionPayload,
    options?: AppointmentSchedulingTransitionOptions,
  ) => {
    const newAppointmentScheduling = await transitionReq.runAsync(event, payload, options);
    // Transitions may mutate other application data. Allow consumers to provide an onBeforeSync
    // callback for any necessary cache-busting (or other followups) before mutating the
    // client's source-of-truth onboarding object.
    if (options?.onBeforeSync) {
      await options.onBeforeSync(newAppointmentScheduling);
    }
    flowReq.mutate(newAppointmentScheduling);
    // This is for logic such as navigating to the correct page after running a transition
    // and having the client’s onboarding object up to date.
    if (options?.onAfterSync) {
      await options.onAfterSync(newAppointmentScheduling);
    }
    return;
  };

  const initSchedulingFlow = async (clinicianRole: number) => {
    await initFlowReq.runAsync({
      clinicianRole,
      therapyOption: isTherapist(getRoleName(clinicianRole))
        ? paymentSelection?.therapy?.option
        : undefined,
    });

    await flowReq.runAsync(clinicianRole);
    initFlowReq.mutate(undefined);
  };

  return (
    <AppointmentSchedulingContext.Provider
      value={{
        appointmentSchedulingFlow: flowReq.data,
        transition,
        initSchedulingFlow,
        isClinicianChangeFlow, // Specific appointment flow type, initiated differently, used to determine how we fetch, etc.
        loading:
          patientSurveyLoading || flowReq.loading || initFlowReq.loading || transitionReq.loading,
        patientSurvey,
        intakeSections: survey?.sections,
        intakeAnswers,
        refreshPatientSurvey,
      }}
    >
      {children}
    </AppointmentSchedulingContext.Provider>
  );
};
